import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { parking_slot_id: "", vehicle_id: "", sticker_number: "", issue_date: new Date().toISOString().slice(0, 10), expiry_date: "", notes: "" };

export default function IssuePassDialog({ open, onOpenChange, slots, onSaved }) {
    const [form, setForm] = useState(EMPTY);
    const [flatVehicles, setFlatVehicles] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const allottedSlots = useMemo(() => slots.filter((s) => s.display_status === "allotted" && s.flat && s.allocation), [slots]);
    const selectedSlot = allottedSlots.find((s) => s.id === form.parking_slot_id);

    useEffect(() => {
        if (!open) { setForm(EMPTY); return; }
        setForm(EMPTY);
    }, [open]);

    useEffect(() => {
        if (!selectedSlot) { setFlatVehicles([]); return; }
        api.get(`/flats/${selectedSlot.flat.id}/parking-vehicles`)
            .then((res) => setFlatVehicles((res.data || []).filter((v) => v.is_active !== false)))
            .catch(() => setFlatVehicles([]));
    }, [selectedSlot]);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    async function handleSubmit(e) {
        e.preventDefault();
        if (submitting || !selectedSlot) return;
        setSubmitting(true);
        try {
            await api.post("/parking/passes", {
                flat_id: selectedSlot.flat.id, parking_slot_id: form.parking_slot_id,
                vehicle_id: form.vehicle_id || null, sticker_number: form.sticker_number || null,
                issue_date: form.issue_date, expiry_date: form.expiry_date || null, notes: form.notes || null,
            });
            toast.success("Parking pass issued");
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Issue parking pass</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Allotted slot *</Label>
                        <Select value={form.parking_slot_id} onValueChange={(v) => set("parking_slot_id", v)} required>
                            <SelectTrigger data-testid="issue-pass-slot-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Select an allotted slot" /></SelectTrigger>
                            <SelectContent>
                                {allottedSlots.length === 0 && <div className="px-3 py-2 text-xs text-brand-inkSoft">No allotted slots available.</div>}
                                {allottedSlots.map((s) => <SelectItem key={s.id} value={s.id}>{s.slot_number} — {s.flat.block}-{s.flat.number}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Vehicle (optional)</Label>
                        <Select value={form.vehicle_id || "__none__"} onValueChange={(v) => set("vehicle_id", v === "__none__" ? "" : v)} disabled={!selectedSlot}>
                            <SelectTrigger data-testid="issue-pass-vehicle-select" className="rounded-sm border-brand-line"><SelectValue placeholder="No vehicle" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">No vehicle</SelectItem>
                                {flatVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_number}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Sticker number (optional)</Label>
                            <Input data-testid="issue-pass-sticker-input" value={form.sticker_number} onChange={(e) => set("sticker_number", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Issue date *</Label>
                            <Input type="date" required data-testid="issue-pass-issue-date-input" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Expiry date (optional)</Label>
                        <Input type="date" data-testid="issue-pass-expiry-date-input" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea data-testid="issue-pass-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="issue-pass-cancel-btn">Cancel</Button>
                        <Button type="submit" disabled={submitting || !selectedSlot} data-testid="issue-pass-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            {submitting ? "Issuing…" : "Issue pass"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
